import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { harvestService } from '../../services/harvestService';
import toast from 'react-hot-toast';

interface HarvestEstimate {
  id: string;
  estimate_number: string;
  client_name: string;
  subject: string;
  amount: number;
  currency: string;
  issue_date: string;
  line_items: any[];
}

interface HarvestImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (estimate: HarvestEstimate) => void;
  harvestClientId?: string; // Optional: filter estimates by client
}

export function HarvestImportModal({ isOpen, onClose, onImport, harvestClientId }: HarvestImportModalProps) {
  const [estimates, setEstimates] = useState<HarvestEstimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEstimate, setSelectedEstimate] = useState<HarvestEstimate | null>(null);
  
  useEffect(() => {
    if (isOpen) {
      loadEstimates();
    }
  }, [isOpen, harvestClientId]);
  
  const loadEstimates = async () => {
    setLoading(true);
    const loadingMsg = harvestClientId 
      ? 'Loading estimates for this client from Harvest...'
      : 'Loading estimates from Harvest...';
    const toastId = toast.loading(loadingMsg);
    
    try {
      const connection = await harvestService.getConnection();
      if (!connection) {
        toast.error('Not connected to Harvest', { id: toastId });
        return;
      }
      
      // Fetch fresh estimates directly from Harvest (optionally filtered by client)
      const estimates = await harvestService.fetchApprovedEstimates(connection.id, harvestClientId);
      setEstimates(estimates);
      
      const successMsg = harvestClientId
        ? `Loaded ${estimates.length} approved estimate(s) for this client`
        : `Loaded ${estimates.length} approved estimates`;
      toast.success(successMsg, { id: toastId });
    } catch (error) {
      console.error('Failed to load estimates:', error);
      toast.error('Failed to load Harvest estimates', { id: toastId });
    } finally {
      setLoading(false);
    }
  };
  
  const filteredEstimates = estimates.filter(est => 
    est.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    est.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    est.estimate_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const handleImport = () => {
    if (selectedEstimate) {
      onImport(selectedEstimate);
      onClose();
    }
  };
  
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-3xl w-full bg-white rounded-lg shadow-xl max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                Import from Harvest
              </Dialog.Title>
              <p className="mt-1 text-sm text-gray-500">
                Select an approved estimate to import
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          
          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by client, subject, or estimate number..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              </div>
            ) : filteredEstimates.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">
                  {searchTerm ? 'No estimates match your search' : 'No approved estimates found'}
                </p>
                <button
                  onClick={loadEstimates}
                  className="mt-4 text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  Refresh from Harvest
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredEstimates.map((estimate) => (
                  <button
                    key={estimate.id}
                    onClick={() => setSelectedEstimate(estimate)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                      selectedEstimate?.id === estimate.id
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-500">
                            #{estimate.estimate_number}
                          </span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-sm text-gray-500">
                            {new Date(estimate.issue_date).toLocaleDateString()}
                          </span>
                        </div>
                        <h3 className="mt-1 font-semibold text-gray-900">
                          {estimate.client_name}
                        </h3>
                        {estimate.subject && (
                          <p className="mt-1 text-sm text-gray-600">
                            {estimate.subject}
                          </p>
                        )}
                        <p className="mt-2 text-sm text-gray-500">
                          {estimate.line_items?.length || 0} line item(s)
                        </p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-lg font-semibold text-gray-900">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: estimate.currency || 'USD',
                          }).format(estimate.amount || 0)}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={!selectedEstimate}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Import Estimate
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

